import { useApi, useMount, useModal, useRouter } from "hooks";
import React, { useCallback, useContext } from "react";
import { useParams } from "react-router-dom";
import lang from "translations";
import ResourceForm from "../resource-form/resource-form";
import { getResource, editResource, deleteResource } from "apis";
import { resourceDetailResponse, resourceRequest } from "mappers";
import { mixpanel, TrackEvent } from "mixpanel";
import { VenueContext } from "contexts";
import { Icon, Text, Title, Toast } from "components/commons";
import { Path } from "paths";
import { DeleteModal } from "components/modals";

const EditResource = () => {
  const { id } = useParams();
  const { venue } = useContext(VenueContext);
  const { venueId } = venue;
  const deleteModal = useModal();
  const { history } = useRouter();

  const { request: getRequest, mappedData: initialData } = useApi({
    api: getResource,
    mapper: resourceDetailResponse,
    params: { venueId },
    handleOwnError: true,
  });

  const { request: editRequest, loading: submitting } = useApi({
    api: editResource,
    handleOwnError: {
      badrequest: true,
    },
    paramsMapper: resourceRequest,
  });

  const { request: requestDeleteResource } = useApi({
    api: () => deleteResource({ resourceId: id }),
  });

  useMount(() => {
    getRequest({ resourceId: id });
    mixpanel.track(TrackEvent.VisitedPage, {
      Page: lang.editResource,
    });
  });

  const submitForm = useCallback(
    async (params) => {
      try {
        const res = await editRequest({ ...params, id });
        return {
          response: res,
          message: lang.populate(lang.resourceUpdated, [params.customName]),
        };
      } catch (e) {
        throw e;
      }
    },
    [editRequest, id]
  );

  return (
    <>
      <ResourceForm
        submit={submitForm}
        title={lang.editResource}
        submitting={submitting}
        initialState={initialData}
        deleteResourceButton={{
          text: lang.deleteResource,
          onClick: () => {
            deleteModal.show({
              ok: async () => {
                mixpanel.track(TrackEvent.ClickedButton, {
                  Button: lang.deleteResource,
                  Page: lang.editResource,
                });

                await requestDeleteResource();
                Toast({
                  content: lang.populate(lang.resourceDeleted, [initialData.defaultName]),
                  success: true,
                  icon: "check",
                }).open();
                history.push(Path.RESOURCE_);
              },
            });
          },
        }}
      />
      <DeleteModal
        primaryText={lang.deleteResource}
        customHeader={
          <div className="flex items-center justify-between">
            <Title lg className="mb-sm pt-md">
              {lang.deleteResource + "?"}
            </Title>
            <Icon
              className="outline-none text-xl text-gray hover:text-gray cursor-pointer"
              name="remove"
              onClick={() => {
                deleteModal.close();
              }}
            />
          </div>
        }
        customContent={
          <>
            <Text>
              {lang.populate(lang.areYouSureYouWantToDeleteResource, [
                initialData.defaultName ,
              ])}
            </Text>
            <br></br>
            <Text>{lang.deletingThisResource}</Text>
          </>
        }
        {...deleteModal}
      />
    </>
  );
};

export default EditResource;
